import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, RefreshCw, Shield, Smartphone, Wallet, Receipt } from 'lucide-react';
import toast from 'react-hot-toast';
import { adminService, CreateManualEcoCashReceiptData, CreateMerchantDeviceData, EcoCashAdminAlert, MerchantDevice } from '../../services/adminService';
import { EcoCashClaimResponse, EcoCashClaimStatus, formatPrice, getEcoCashClaimStatusLabel } from '../../types/payments';

interface AdminEcoCashOperationsProps {
  isDark: boolean;
}

const dateText = (value?: string) => {
  if (!value) return 'Not yet';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const alertTone = (severity: string) => {
  switch (severity) {
    case 'high':
      return 'border-red-500/30 bg-red-500/10 text-red-300';
    case 'medium':
      return 'border-amber-500/30 bg-amber-500/10 text-amber-300';
    default:
      return 'border-slate-500/30 bg-slate-500/10 text-slate-300';
  }
};

const deviceTone = (status: string) => {
  switch (status) {
    case 'healthy':
      return 'text-emerald-400';
    case 'degraded':
      return 'text-amber-400';
    case 'offline':
    case 'stale':
      return 'text-red-400';
    default:
      return 'text-slate-400';
  }
};

const claimActions: Array<{ label: string; action: 'approve' | 'reject' | 'manual_review' }> = [
  { label: 'Approve', action: 'approve' },
  { label: 'Hold', action: 'manual_review' },
  { label: 'Reject', action: 'reject' },
];

const AdminEcoCashOperations: React.FC<AdminEcoCashOperationsProps> = ({ isDark }) => {
  const queryClient = useQueryClient();
  const [deviceLabel, setDeviceLabel] = useState('Primary EcoCash Phone');
  const [manualReceipt, setManualReceipt] = useState<CreateManualEcoCashReceiptData>({
    ingest_key: `manual-${Date.now()}`,
    source: 'sms',
    raw_message: '',
  });
  const [rotatedCredentials, setRotatedCredentials] = useState<{ apiKey: string; hmacSecret: string; deviceLabel: string } | null>(null);

  const cardClass = isDark
    ? 'bg-white/5 border-white/10'
    : 'bg-white border-slate-200 shadow-sm';

  const queries = {
    alerts: useQuery({
      queryKey: ['adminEcocash', 'alerts'],
      queryFn: adminService.getEcoCashAlerts,
    }),
    devices: useQuery({
      queryKey: ['adminEcocash', 'devices'],
      queryFn: adminService.getEcoCashDevices,
    }),
    claims: useQuery({
      queryKey: ['adminEcocash', 'claims'],
      queryFn: adminService.getEcoCashClaims,
    }),
    receipts: useQuery({
      queryKey: ['adminEcocash', 'receipts'],
      queryFn: adminService.getEcoCashReceipts,
    }),
    audit: useQuery({
      queryKey: ['adminEcocash', 'audit'],
      queryFn: adminService.getEcoCashAuditEvents,
    }),
  };

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['adminEcocash', 'alerts'] }),
      queryClient.invalidateQueries({ queryKey: ['adminEcocash', 'devices'] }),
      queryClient.invalidateQueries({ queryKey: ['adminEcocash', 'claims'] }),
      queryClient.invalidateQueries({ queryKey: ['adminEcocash', 'receipts'] }),
      queryClient.invalidateQueries({ queryKey: ['adminEcocash', 'audit'] }),
    ]);
  };

  const createDevice = useMutation({
    mutationFn: (payload: CreateMerchantDeviceData) => adminService.createEcoCashDevice(payload),
    onSuccess: async (device) => {
      toast.success(`Created enrollment for ${device.device_label}.`);
      setDeviceLabel(`Cold Spare ${Math.floor(Math.random() * 9) + 1}`);
      await invalidateAll();
    },
    onError: () => toast.error('Could not create merchant device enrollment.'),
  });

  const rotateCredentials = useMutation({
    mutationFn: async (device: MerchantDevice) => {
      const rotated = await adminService.rotateEcoCashDeviceCredentials(device.id);
      return { rotated, device };
    },
    onSuccess: async ({ rotated, device }) => {
      setRotatedCredentials({
        apiKey: rotated.api_key,
        hmacSecret: rotated.hmac_secret,
        deviceLabel: device.device_label,
      });
      toast.success(`Rotated credentials for ${device.device_label}.`);
      await invalidateAll();
    },
    onError: () => toast.error('Could not rotate merchant device credentials.'),
  });

  const reviewClaim = useMutation({
    mutationFn: ({ claimId, action }: { claimId: string; action: 'approve' | 'reject' | 'manual_review' }) =>
      adminService.reviewEcoCashClaim(claimId, action),
    onSuccess: async () => {
      toast.success('EcoCash claim updated.');
      await invalidateAll();
    },
    onError: () => toast.error('Could not update EcoCash claim.'),
  });

  const manualReceiptMutation = useMutation({
    mutationFn: (payload: CreateManualEcoCashReceiptData) => adminService.createManualEcoCashReceipt(payload),
    onSuccess: async () => {
      toast.success('Manual EcoCash receipt recorded.');
      setManualReceipt({
        ingest_key: `manual-${Date.now()}`,
        source: 'sms',
        raw_message: '',
      });
      await invalidateAll();
    },
    onError: () => toast.error('Could not record the manual receipt.'),
  });

  const pendingClaims = useMemo(
    () => (queries.claims.data || []).filter((claim) => claim.claim_status !== EcoCashClaimStatus.APPROVED).slice(0, 8),
    [queries.claims.data]
  );

  return (
    <div className={`backdrop-blur-sm rounded-2xl border transition-colors ${cardClass}`}>
      <div className={`p-5 border-b ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>EcoCash Operations</h3>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Merchant device health, claim review, and manual fallback tools.
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => invalidateAll()}
            className={`px-3 py-2 rounded-xl text-sm flex items-center gap-2 border ${
              isDark ? 'border-white/10 text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="p-5 space-y-6">
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Current Alerts</h4>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {(queries.alerts.data || []).length === 0 ? (
              <div className={`rounded-xl border p-4 text-sm ${isDark ? 'border-white/10 text-slate-400' : 'border-slate-200 text-slate-500'}`}>
                No active EcoCash alerts right now.
              </div>
            ) : (
              (queries.alerts.data || []).map((alert: EcoCashAdminAlert) => (
                <div key={alert.id} className={`rounded-xl border p-4 ${alertTone(alert.severity)}`}>
                  <p className="font-medium">{alert.title}</p>
                  <p className="mt-1 text-sm opacity-90">{alert.detail}</p>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50/60'}`}>
            <div className="flex items-center gap-2 mb-4">
              <Smartphone className="w-4 h-4 text-cyan-400" />
              <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Merchant Devices</h4>
            </div>

            <div className="flex gap-2 mb-4">
              <input
                value={deviceLabel}
                onChange={(event) => setDeviceLabel(event.target.value)}
                placeholder="Dedicated EcoCash Phone"
                className={`flex-1 rounded-xl px-3 py-2 text-sm border ${
                  isDark ? 'bg-slate-950/60 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-800'
                }`}
              />
              <button
                onClick={() => createDevice.mutate({ device_label: deviceLabel })}
                disabled={createDevice.isPending || !deviceLabel.trim()}
                className="px-3 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-medium disabled:opacity-60"
              >
                Add Device
              </button>
            </div>

            <div className="space-y-3">
              {(queries.devices.data || []).map((device) => (
                <div key={device.id} className={`rounded-xl border p-4 ${isDark ? 'border-white/10 bg-black/10' : 'border-slate-200 bg-white'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{device.device_label}</p>
                      <p className={`text-xs ${deviceTone(device.health_status)}`}>{device.health_status}</p>
                      <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        Enrollment code: <span className="font-mono">{device.enrollment_code || 'None'}</span>
                      </p>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                        Last heartbeat: {dateText(device.last_heartbeat_at)}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                        Queue: {device.last_queue_size ?? 0} | Battery: {device.last_battery_level ?? 'n/a'}%
                      </p>
                    </div>
                    <button
                      onClick={() => rotateCredentials.mutate(device)}
                      disabled={rotateCredentials.isPending}
                      className={`px-3 py-2 rounded-xl text-xs border ${
                        isDark ? 'border-white/10 text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      Rotate Keys
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {rotatedCredentials && (
              <div className={`mt-4 rounded-xl border p-4 ${isDark ? 'border-emerald-500/20 bg-emerald-500/10' : 'border-emerald-200 bg-emerald-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-emerald-400" />
                  <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    New credentials for {rotatedCredentials.deviceLabel}
                  </p>
                </div>
                <p className="text-xs font-mono break-all">{rotatedCredentials.apiKey}</p>
                <p className="text-xs font-mono break-all mt-2">{rotatedCredentials.hmacSecret}</p>
              </div>
            )}
          </div>

          <div className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50/60'}`}>
            <div className="flex items-center gap-2 mb-4">
              <Receipt className="w-4 h-4 text-violet-400" />
              <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Manual Receipt Fallback</h4>
            </div>

            <textarea
              value={manualReceipt.raw_message}
              onChange={(event) => setManualReceipt((current) => ({ ...current, raw_message: event.target.value }))}
              rows={6}
              placeholder="Paste the full EcoCash SMS here if the merchant phone failed to sync it."
              className={`w-full rounded-xl px-3 py-2 text-sm border ${
                isDark ? 'bg-slate-950/60 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-800'
              }`}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
              <input
                value={manualReceipt.sender_address || ''}
                onChange={(event) => setManualReceipt((current) => ({ ...current, sender_address: event.target.value || undefined }))}
                placeholder="Sender address"
                className={`rounded-xl px-3 py-2 text-sm border ${
                  isDark ? 'bg-slate-950/60 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-800'
                }`}
              />
              <input
                value={manualReceipt.ingest_key}
                onChange={(event) => setManualReceipt((current) => ({ ...current, ingest_key: event.target.value }))}
                placeholder="Ingest key"
                className={`rounded-xl px-3 py-2 text-sm border ${
                  isDark ? 'bg-slate-950/60 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-800'
                }`}
              />
            </div>
            <button
              onClick={() => manualReceiptMutation.mutate(manualReceipt)}
              disabled={manualReceiptMutation.isPending || !manualReceipt.raw_message.trim() || !manualReceipt.ingest_key.trim()}
              className="mt-3 px-3 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-medium disabled:opacity-60"
            >
              Save Manual Receipt
            </button>
          </div>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50/60'}`}>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Claim Review Queue</h4>
            </div>
            <div className="space-y-3">
              {pendingClaims.length === 0 ? (
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>No pending EcoCash claims right now.</p>
              ) : (
                pendingClaims.map((claim: EcoCashClaimResponse) => (
                  <div key={claim.id} className={`rounded-xl border p-4 ${isDark ? 'border-white/10 bg-black/10' : 'border-slate-200 bg-white'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                          {claim.transaction_id || claim.checkout_reference || claim.id}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {getEcoCashClaimStatusLabel(claim.claim_status)} | {formatPrice(claim.requested_amount, claim.currency)}
                        </p>
                        {claim.reason_code && (
                          <p className={`text-xs font-mono ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{claim.reason_code}</p>
                        )}
                        {claim.feedback_reason && (
                          <p className={`mt-1 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{claim.feedback_reason}</p>
                        )}
                      </div>
                      <div className="flex flex-wrap justify-end gap-2">
                        {claimActions.map((item) => (
                          <button
                            key={item.action}
                            onClick={() => reviewClaim.mutate({ claimId: claim.id, action: item.action })}
                            disabled={reviewClaim.isPending}
                            className={`px-2.5 py-1.5 rounded-lg text-xs border ${
                              isDark ? 'border-white/10 text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50/60'}`}>
            <div className="flex items-center gap-2 mb-4">
              <Receipt className="w-4 h-4 text-cyan-400" />
              <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Latest Receipts</h4>
            </div>
            <div className="space-y-3 max-h-[360px] overflow-y-auto">
              {(queries.receipts.data || []).slice(0, 8).map((receipt) => (
                <div key={receipt.id} className={`rounded-xl border p-3 ${isDark ? 'border-white/10 bg-black/10' : 'border-slate-200 bg-white'}`}>
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    {receipt.transaction_id || receipt.id}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {formatPrice(receipt.amount || 0, receipt.currency || 'USD')} | {receipt.parse_status} | {receipt.duplicate_status}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{dateText(receipt.created_at)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50/60'}`}>
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-slate-400" />
            <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Audit Trail</h4>
          </div>
          <div className="space-y-2 max-h-[260px] overflow-y-auto">
            {(queries.audit.data || []).slice(0, 10).map((event) => (
              <div key={event.id} className={`rounded-xl border p-3 ${isDark ? 'border-white/10 bg-black/10' : 'border-slate-200 bg-white'}`}>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{event.event_type}</p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{event.message || `${event.entity_type} ${event.entity_id}`}</p>
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{dateText(event.created_at)}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminEcoCashOperations;
